import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft, 
  Reply, 
  Star, 
  MoreHorizontal,
  User,
  Users,
  Calendar,
  Paperclip,
  Download,
  Mail,
  MailOpen,
  Check,
  Brain
} from 'lucide-react';
import { Email } from '../../types';
import { formatDate, getStatusText, cn } from '../../utils';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { formatRelativeTime, formatDateTime } from '../../utils/timezone';
import { getApiBaseUrl } from '../../config/env';
import { useSettings } from '../../hooks/useSettings';
import '../../styles/email-content.css';

interface EmailDetailViewProps {
  email: Email | null;
  onBack: () => void;
  onReply?: (email: Email) => void;
  onEmailUpdate?: (emailId: number, updates: Partial<Email>) => void;
  onAIAnalysis?: (email: Email) => void;
}

// AI分析结果接口
interface AIAnalysisResult {
  id: number;
  email_id: number;
  translated_content?: string;
  chinese_translation?: string;
  summary?: string;
  chinese_summary?: string;
  category?: string;
  sentiment?: string;
  priority_score?: number;
  tags: string[];
  suggested_reply?: string;
  reply_chinese_translation?: string;
  reply_tone?: string;
  confidence_scores: Record<string, number>;
  created_at: string;
}

const EmailDetailView: React.FC<EmailDetailViewProps> = ({
  email,
  onBack,
  onReply,
  onEmailUpdate,
  onAIAnalysis
}) => {
  const { getSetting } = useSettings();
  const [showRawContent, setShowRawContent] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [markingAsUnread, setMarkingAsUnread] = useState(false);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState<AIAnalysisResult | null>(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);

  // 自动标记为已读
  useEffect(() => {
    if (email && email.status === 'unread') {
      // 检查设置是否启用自动标记已读
      const autoMarkReadEnabled = getSetting('autoMarkReadOnOpen');
      
      if (autoMarkReadEnabled) {
        // 延迟1秒后自动标记为已读，给用户时间看到未读状态
        const timer = setTimeout(() => {
          handleMarkAsRead(true); // 传入true表示是自动标记
        }, 1000);

        return () => clearTimeout(timer);
      }
    }
  }, [email?.id, email?.status, getSetting]); // 添加getSetting依赖

  // 获取AI分析结果
  useEffect(() => {
    if (email?.id) {
      fetchAIAnalysisResult(email.id);
    }
  }, [email?.id]);

  const fetchAIAnalysisResult = async (emailId: number) => {
    try {
      setAiAnalysisLoading(true);
      const response = await fetch(`${getApiBaseUrl()}/ai/email-analysis/${emailId}`);
      
      if (response.ok) {
        const result = await response.json();
        setAiAnalysisResult(result);
      } else if (response.status === 404) {
        // 没有分析结果，这是正常情况
        setAiAnalysisResult(null);
      } else {
        console.error('获取AI分析结果失败:', response.status);
      }
    } catch (error) {
      console.error('获取AI分析结果失败:', error);
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const handleMarkAsRead = async (isAutomatic = false) => {
    if (!email) return;
    
    setMarkingAsRead(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/emails/${email.id}/status?status=read`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // 使用回调更新邮件状态
        onEmailUpdate?.(email.id, { status: 'read' });
      } else {
        throw new Error('标记为已读失败');
      }
    } catch (error) {
      console.error('标记为已读失败:', error);
      if (!isAutomatic) {
      alert('标记为已读失败，请重试');
      }
    } finally {
      setMarkingAsRead(false);
    }
  };

  const handleMarkAsUnread = async () => {
    if (!email) return;
    
    setMarkingAsUnread(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/emails/${email.id}/status?status=unread`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // 使用回调更新邮件状态
        onEmailUpdate?.(email.id, { status: 'unread' });
      } else {
        throw new Error('标记为未读失败');
      }
    } catch (error) {
      console.error('标记为未读失败:', error);
      alert('标记为未读失败，请重试');
    } finally {
      setMarkingAsUnread(false);
    }
  };

  const handleAIAnalysis = async () => {
    if (!email || aiAnalyzing) return;
    
    setAiAnalyzing(true);
    try {
      // 调用AI分析API - 使用新的触发助手API
      const response = await fetch(`${getApiBaseUrl()}/ai/trigger-assistant/1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email_id: email.id })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('AI分析完成:', result);
        
        // 等待一段时间让后端处理完成
        setTimeout(async () => {
          // 重新获取分析结果
          await fetchAIAnalysisResult(email.id);
          
          // 自动显示分析结果
          setShowAiAnalysis(true);
        }, 2000); // 等待2秒
      } else {
        throw new Error('AI分析请求失败');
      }
    } catch (error) {
      console.error('AI分析失败:', error);
      alert('AI分析失败，请稍后重试');
    } finally {
      setAiAnalyzing(false);
    }
  };

  const toggleAIAnalysis = () => {
    if (aiAnalysisResult) {
      setShowAiAnalysis(!showAiAnalysis);
    } else {
      handleAIAnalysis();
    }
  };

  // 重新进行AI分析
  const handleReAnalysis = async () => {
    if (!email || aiAnalyzing) return;
    
    setAiAnalyzing(true);
    try {
      // 调用AI分析API - 使用新的触发助手API
      const response = await fetch(`${getApiBaseUrl()}/ai/trigger-assistant/1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email_id: email.id })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('AI重新分析完成:', result);
        
        // 等待一段时间让后端处理完成
        setTimeout(async () => {
          // 重新获取分析结果
          await fetchAIAnalysisResult(email.id);
          
          // 自动显示分析结果
          setShowAiAnalysis(true);
        }, 2000); // 等待2秒
      } else {
        throw new Error('AI分析请求失败');
      }
    } catch (error) {
      console.error('AI重新分析失败:', error);
      alert('AI重新分析失败，请稍后重试');
    } finally {
      setAiAnalyzing(false);
    }
  };

  const handleMarkAsReadClick = () => handleMarkAsRead(false);

  const sanitizeHTML = (html: string): string => {
    // 基本的HTML清理，实际项目中应使用专业的清理库如DOMPurify
    return html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '');
  };

  const isRealHTML = (html: string): boolean => {
    if (!html) return false;
    
    // 检查是否包含HTML标签
    const hasHTMLTags = /<[^>]+>/i.test(html);
    
    // 检查是否包含常见的HTML标签
    const hasCommonTags = /<(div|span|p|br|strong|b|em|i|u|a|img|table|tr|td|th|ul|ol|li|h[1-6]|blockquote|style|link)\b[^>]*>/i.test(html);
    
    return hasHTMLTags && hasCommonTags;
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'unread': return '未读';
      case 'read': return '已读';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return formatRelativeTime(dateString);
  };

  return (
    <div className="flex flex-col h-full theme-bg-primary">
      {/* 头部工具栏 */}
      <div className="border-b theme-border-primary p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回
            </Button>
            
            <div className="h-6 w-px theme-bg-tertiary" />
            
            {/* 已读/未读状态和操作 */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 text-sm theme-text-secondary">
                {email?.status === 'unread' ? (
                  <Mail className="w-4 h-4 theme-blue-text" />
                ) : (
                  <MailOpen className="w-4 h-4 theme-text-secondary" />
                )}
                <span>{email?.status === 'unread' ? '未读' : '已读'}</span>
              </div>
              
              {email?.status === 'unread' ? (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleMarkAsReadClick}
                  loading={markingAsRead}
                  className="theme-blue-text hover:theme-blue-text"
                >
                  <Check className="w-4 h-4 mr-1" />
                  标记为已读
                </Button>
              ) : (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleMarkAsUnread}
                  loading={markingAsUnread}
                  className="theme-text-secondary hover:theme-text-primary"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  标记为未读
                </Button>
              )}
            </div>
            
            <div className="h-6 w-px theme-bg-tertiary" />
            
            <Button variant="ghost" size="sm">
              <Star className="w-4 h-4 mr-2" />
              标星
            </Button>

            <div className="h-6 w-px theme-bg-tertiary" />
            
            {/* AI分析按钮 */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleAIAnalysis}
              disabled={aiAnalyzing || aiAnalysisLoading}
              className={cn(
                "theme-transition",
                aiAnalysisResult 
                  ? showAiAnalysis 
                    ? "text-purple-800 bg-purple-100 hover:bg-purple-200" 
                    : "text-purple-600 hover:text-purple-800"
                  : "text-purple-600 hover:text-purple-800"
              )}
            >
              <Brain className="w-4 h-4 mr-2" />
              {aiAnalyzing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                  AI分析中...
                </>
              ) : aiAnalysisLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                  加载中...
                </>
              ) : aiAnalysisResult ? (
                showAiAnalysis ? '收起分析' : '查看分析'
              ) : (
                'AI分析'
              )}
            </Button>
          </div>

          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* 邮件内容 */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-6">
          {/* 邮件头部信息 */}
          <div className="mb-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3 flex-1 mr-4">
                {/* 未读指示器 */}
                {email?.status === 'unread' && (
                  <div className="w-3 h-3 bg-blue-500 rounded-full flex-shrink-0" title="未读邮件"></div>
                )}
                <h1 className={cn(
                  'text-2xl theme-text-primary flex-1',
                  email?.status === 'unread' ? 'font-bold' : 'font-semibold'
                )}>
                  {email?.subject}
                </h1>
              </div>
              <div className="flex items-center space-x-2">
                <Badge 
                  variant={
                  email?.status === 'unread' ? 'warning' :
                  email?.status === 'read' ? 'success' : 'default'
                  }
                >
                  {getStatusText(email?.status || 'default')}
                </Badge>
                {email?.status === 'unread' && (
                  <Badge variant="warning" size="sm">
                    未读
                  </Badge>
                )}
              </div>
            </div>

            {/* 如果是未读邮件，显示醒目的提示条 */}
            {email?.status === 'unread' && (
              <div className="theme-blue-bg border theme-blue-border rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 theme-blue-text" />
                    <span className="text-sm font-medium theme-blue-text">
                      这是一封未读邮件
                    </span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleMarkAsReadClick}
                    loading={markingAsRead}
                    className="theme-blue-border theme-blue-text theme-blue-bg-hover"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    标记为已读
                  </Button>
                </div>
              </div>
            )}

            <div className={cn(
              'rounded-lg p-4',
              email?.status === 'unread' ? 'theme-blue-bg border theme-blue-border' : 'theme-bg-secondary'
            )}>
              <div className="flex items-start space-x-4">
                <div className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  email?.status === 'unread' ? 'bg-blue-200' : 'bg-blue-100'
                )}>
                  {email?.is_user_email ? (
                    <User className="w-5 h-5 theme-blue-text" />
                  ) : (
                    <Users className="w-5 h-5 theme-text-secondary" />
                  )}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className={cn(
                        'theme-text-primary',
                        email?.status === 'unread' ? 'font-semibold' : 'font-medium'
                      )}>
                        {email?.sender_name || email?.sender_email}
                      </h3>
                      <p className="text-sm theme-text-tertiary">
                        {email?.sender_email}
                      </p>
                    </div>
                    
                    <div className="text-right">
                      <div className="flex items-center text-sm theme-text-tertiary mb-1">
                        <Calendar className="w-4 h-4 mr-1" />
                        {email?.received_at ? formatDate(email.received_at) : '未知时间'}
                      </div>
                      {email?.is_user_email && (
                        <Badge variant="success" size="sm">
                          用户邮件
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {email?.sender_rule && (
                    <div className="text-sm theme-text-secondary">
                      <span className="font-medium">规则匹配:</span> 
                      <Badge variant="info" size="sm" className="ml-2">
                        {email?.sender_rule.rule_type}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 邮件正文 */}
          <div className="mb-6">
            <div className="prose max-w-none">
              {/* 调试信息
              {process.env.NODE_ENV === 'development' && (
                <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                  <div>HTML Content: {email?.html_content ? 'Yes' : 'No'}</div>
                  <div>Is Real HTML: {email?.html_content ? isRealHTML(email?.html_content).toString() : 'N/A'}</div>
                  <div>Content Length: {email?.content?.length || 0}</div>
                </div>
              )} */}
              
              {email?.html_content && isRealHTML(email?.html_content) ? (
                <div className="border rounded-lg p-4 theme-bg-primary">
                  <div className="flex items-center justify-between mb-4 pb-2 border-b">
                    <span className="text-sm font-medium theme-text-primary">HTML 内容</span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowRawContent(!showRawContent)}
                        className="text-sm theme-blue-text hover:theme-blue-text theme-transition"
                      >
                        {showRawContent ? '显示渲染内容' : '显示原始代码'}
                      </button>
                      <Button variant="ghost" size="sm">
                        <Download className="w-4 h-4 mr-1" />
                        下载
                      </Button>
                    </div>
                  </div>
                  
                  {showRawContent ? (
                    <pre className="whitespace-pre-wrap text-sm theme-bg-secondary p-4 rounded overflow-auto max-h-96">
                      {email?.html_content}
                    </pre>
                  ) : (
                    <div 
                      className="email-content"
                      dangerouslySetInnerHTML={{ 
                        __html: sanitizeHTML(email?.html_content) 
                      }} 
                    />
                  )}
                </div>
              ) : email?.content ? (
                <div className="border rounded-lg p-4 theme-bg-primary">
                  <div className="flex items-center justify-between mb-4 pb-2 border-b">
                    <span className="text-sm font-medium theme-text-primary">纯文本内容</span>
                  </div>
                  <div className="whitespace-pre-wrap theme-text-primary leading-relaxed">
                    {email?.content}
                  </div>
                </div>
              ) : (
                <div className="border rounded-lg p-4 theme-bg-secondary text-center">
                  <p className="theme-text-tertiary">暂无邮件内容</p>
                </div>
              )}
            </div>
          </div>

          {/* AI分析结果 */}
          {showAiAnalysis && aiAnalysisResult && (
            <div className="mb-6">
              <div className="border rounded-lg p-4 bg-gradient-to-r from-purple-50 to-blue-50">
                <div className="flex items-center justify-between mb-4 pb-2 border-b border-purple-200">
                  <div className="flex items-center space-x-2">
                    <Brain className="w-5 h-5 text-purple-600" />
                    <span className="text-lg font-semibold text-purple-800">AI 分析结果</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleReAnalysis}
                      disabled={aiAnalyzing}
                      className="text-purple-600 hover:text-purple-800"
                    >
                      {aiAnalyzing ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600 mr-1"></div>
                          重新分析中...
                        </>
                      ) : (
                        '重新分析'
                      )}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowAiAnalysis(false)}
                      className="text-purple-600 hover:text-purple-800"
                    >
                      收起
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* 中文翻译 */}
                  {aiAnalysisResult.chinese_translation && (
                    <div className="bg-white rounded-lg p-4 border border-purple-100">
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        中文翻译
                      </h4>
                      <p className="text-gray-700 leading-relaxed">{aiAnalysisResult.chinese_translation}</p>
                    </div>
                  )}

                  {/* 摘要 */}
                  {aiAnalysisResult.chinese_summary && (
                    <div className="bg-white rounded-lg p-4 border border-purple-100">
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        内容摘要
                      </h4>
                      <p className="text-gray-700 leading-relaxed">{aiAnalysisResult.chinese_summary}</p>
                    </div>
                  )}

                  {/* 分类和情感分析 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {aiAnalysisResult.category && (
                      <div className="bg-white rounded-lg p-4 border border-purple-100">
                        <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                          邮件分类
                        </h4>
                        <Badge variant="info" className="text-sm">
                          {aiAnalysisResult.category}
                        </Badge>
                      </div>
                    )}

                    {aiAnalysisResult.sentiment && (
                      <div className="bg-white rounded-lg p-4 border border-purple-100">
                        <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                          <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                          情感分析
                        </h4>
                        <Badge 
                          variant={
                            aiAnalysisResult.sentiment === 'positive' ? 'success' :
                            aiAnalysisResult.sentiment === 'negative' ? 'danger' : 'default'
                          }
                          className="text-sm"
                        >
                          {aiAnalysisResult.sentiment === 'positive' ? '积极' :
                           aiAnalysisResult.sentiment === 'negative' ? '消极' :
                           aiAnalysisResult.sentiment === 'neutral' ? '中性' : aiAnalysisResult.sentiment}
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* 优先级和标签 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {aiAnalysisResult.priority_score !== undefined && (
                      <div className="bg-white rounded-lg p-4 border border-purple-100">
                        <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                          <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                          优先级评分
                        </h4>
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${(aiAnalysisResult.priority_score / 10) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            {aiAnalysisResult.priority_score}/10
                          </span>
                        </div>
                      </div>
                    )}

                    {aiAnalysisResult.tags && aiAnalysisResult.tags.length > 0 && (
                      <div className="bg-white rounded-lg p-4 border border-purple-100">
                        <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                          <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                          标签
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {aiAnalysisResult.tags.map((tag, index) => (
                            <Badge key={index} variant="default" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 建议回复 */}
                  {(aiAnalysisResult.reply_chinese_translation || aiAnalysisResult.suggested_reply) && (
                    <div className="bg-white rounded-lg p-4 border border-purple-100">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-teal-500 rounded-full mr-2"></span>
                        建议回复
                      </h4>
                      
                      {/* 中文回复 */}
                      {aiAnalysisResult.reply_chinese_translation && (
                        <div className="mb-3">
                          <div className="text-sm text-gray-600 mb-1 font-medium">中文回复：</div>
                          <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-teal-400">
                            <p className="text-gray-700 leading-relaxed">{aiAnalysisResult.reply_chinese_translation}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* 英文回复 */}
                      {aiAnalysisResult.suggested_reply && (
                        <div className="mb-3">
                          <div className="text-sm text-gray-600 mb-1 font-medium">English Reply:</div>
                          <div className="bg-blue-50 rounded-lg p-3 border-l-4 border-blue-400">
                            <p className="text-gray-700 leading-relaxed">{aiAnalysisResult.suggested_reply}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* 语调信息 */}
                      {aiAnalysisResult.reply_tone && (
                        <div className="mt-2">
                          <Badge variant="info" size="sm">
                            语调: {aiAnalysisResult.reply_tone}
                          </Badge>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 分析时间 */}
                  <div className="text-xs text-gray-500 text-center pt-2 border-t border-purple-100">
                    分析时间: {formatDate(aiAnalysisResult.created_at)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 操作按钮 */}
          <div className="flex items-center space-x-4 pt-6 border-t theme-border-primary">
            <Button onClick={() => email && onReply?.(email)}>
              <Reply className="w-4 h-4 mr-2" />
              回复
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailDetailView; 